import React, { useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { projects, stats, services } from '../data/mockData';
import { initScrollAnimations, animateCounters, createParticles } from '../utils/scrollAnimations';

const Home = () => {
  const featuredProjects = projects.slice(0, 3);
  const navigate = useNavigate();

  useEffect(() => {
    // Initialize scroll animations
    initScrollAnimations();
    
    // Create particles in hero section
    const heroSection = document.querySelector('.hero-section');
    if (heroSection) {
      const particlesContainer = document.createElement('div');
      particlesContainer.className = 'particles-bg';
      heroSection.appendChild(particlesContainer);
      createParticles(particlesContainer);
    }
    
    // Animate counters when stats section is visible
    const statsSection = document.querySelector('.stats-section');
    if (statsSection) {
      const statsObserver = new IntersectionObserver((entries) => {
        entries.forEach(entry => {
          if (entry.isIntersecting) {
            animateCounters();
            statsObserver.unobserve(entry.target);
          }
        });
      });
      statsObserver.observe(statsSection);
    }
    
    return () => {};
  }, []);

  const reviews = [
    {
      name: 'Sonny & Marilyn C',
      review: 'Osley & Associates built our first new custom home in Duluth Georgia in 1994. When I retired we bought an older Osley built home in Dahlonega GA and had them remodel it for us to live in while they built our ”forever” dream home. I can’t say enough good things about the three generations of Osleys.  The are absolutely top drawer builders.  They use the highest quality materials, suppliers and subcontractors available and produce a superior product on schedule and within the budget. I enthusiastically recommend them for your new home!',
      location: 'Dahlonega, GA'
    },
    {
      name: 'Mike & Lori M',
      review: 'Building a custom home can be stressful and daunting, but Osley and Associates not only took away that stress due to their long history of home building, but they also made it a fun experience! With that long history comes a team of trusted subcontractors and suppliers who also have a long history of working closely with the Osleys. Seeing Stephen or Cam nearly every day at our homesite, and getting to know their subs (the very hands that built our home) on a personal basis truly made our homebuilding experience so wonderful! Even after our home was finished, Stephen and Cam proactively continue to check in to ensure all is going well-- something other builders don\'t do. There\'s more to building a home than just hammers and nails-- the Osley team brings the heart to every home they build.',
      location: ''
    },
    {
      name: 'George & Ellen B',
      review: 'Choosing the Osleys to build our new home is one of the best decisions we could have made.   They were able to incorporate all of our wants and needs into the initial design, sometimes suggesting improvements on our own ideas.  They made sure that we were involved in the entire process, start to finish.  Not only do we have an amazing new home that far exceeds anything we could have imagined, we also have a lasting friendship with the Osley family.',
      location: ''
    },
    {
      name: 'Lee & Maureen R',
      review: 'The experience building our home with Garry and Stephen Osley was positive and satisfying. From start to finish, their skill, guidance, creativity, and professionalism were evident in every phase of the project. The entire Osley team was responsive, transparent, and genuinely cared about bringing our vision to life. We are truly thrilled with our new home and feel fortunate to have worked with builders who take such pride in their work.',
      location: 'Marietta, GA'
    }
  ];

  return (
    <div className="home-page">
      {/* Hero Section */}
      <section className="hero-section" style={{ position: 'relative', minHeight: '100vh', display: 'flex', alignItems: 'center', justifyContent: 'center', overflow: 'hidden' }}>
        <img
          src="https://degbfm0bobp7.cloudfront.net/storage/1003328c-3f5d-4d10-abc9-30861628ca8f/delivery/4c562360-5c1b-4cf4-b408-37e49df790f8/images/thumbnails/003_dmBl.jpg"
          alt="Luxury Home Hero"
          style={{
            position: 'absolute',
            top: 0,
            left: 0,
            width: '100%',
            height: '100%',
            objectFit: 'cover',
            zIndex: 1,
            filter: 'brightness(0.7)'
          }}
        />
        <div
          style={{
            position: 'absolute',
            top: 0,
            left: 0,
            width: '100%',
            height: '100%',
            background: 'rgba(10, 15, 26, 0.6)',
            zIndex: 2
          }}
        />
        <div className="container" style={{ position: 'relative', zIndex: 3 }}>
          <div className="hero-content">
            <h1 className="hero-title floating-element">Luxury Custom Home Builders</h1>
            <p className="hero-subtitle animate-on-scroll">
              Osley & Associates specializes in creating exceptional custom homes that blend modern sophistication with timeless design. From contemporary masterpieces to charming farmhouse estates.
            </p>
            <div className="hero-buttons animate-on-scroll">
              <Link to="/projects" className="btn btn-primary">View Our Projects</Link>
              <Link to="/contact" className="btn btn-secondary">Start Your Project</Link>
            </div>
          </div>
        </div>
      </section>

      {/* Stats Section */}
      <section className="stats-section">
        <div className="container">
          <div className="grid grid-4">
            {stats.map((stat, index) => (
              <div key={stat.id} className={`stat-item animate-on-scroll ${index % 2 === 0 ? 'slide-left' : 'slide-right'}`}>
                <div className="stat-number">{stat.number}</div>
                <div className="stat-label">{stat.label}</div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Reviews Section */}
      <section className="section" style={{ paddingTop: 0 }}>
        <div className="container">
          <div style={{ textAlign: 'center' }}>
            <h2 className="section-title" style={{ marginBottom: '2.5rem' }}>What our clients say</h2>
          </div>
          <div className="grid grid-2">
            {reviews.map((review, idx) => (
              <div key={idx} className="review-card">
                <div className="review-content" style={{display: 'flex', flexDirection: 'column', height: '100%'}}>
                  <p className="review-text" style={{flexGrow: 1}}>
                    "{review.review}"
                  </p>
                  <div className="review-author" style={{marginTop: 'auto'}}>
                    - {review.name}.
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>


      {/* Featured Projects */}
      <section className="section" style={{ background: 'var(--off-white)' }}>
        <div className="container">
          <div className="text-center">
            <h2 className="section-title animate-on-scroll">Featured Projects</h2>
            <p className="section-subtitle animate-on-scroll">
              Explore some of our most recent luxury home projects that showcase our commitment to excellence and attention to detail.
            </p>
          </div>
          <div className="grid grid-3">
            {featuredProjects.map((project, index) => (
              <div
                key={project.id}
                className={`project-card animate-on-scroll ${index % 3 === 0 ? 'slide-left' : index % 3 === 1 ? 'scale-in' : 'slide-right'}`}
                style={{ cursor: 'pointer' }}
                onClick={() => navigate(`/projects?projectId=${project.id}`)}
              >
                <img src={project.image} alt={project.title} className="project-image" />
                <div className="project-overlay">
                  <h3 className="project-title" style={{ color: '#fff' }}>{project.title}</h3>
                  <p className="project-description" style={{ color: '#fff' }}>{project.description}</p>
                  <div className="project-meta">

                    <span className="project-size" style={{ color: '#fff' }}>{project.size}</span>
                  </div>
                </div>
              </div>
            ))}
          </div>
          <div className="text-center animate-on-scroll" style={{ marginTop: '40px' }}>
            <Link to="/projects" className="btn btn-primary">View All Projects</Link>
          </div>
        </div>
      </section>

      {/* About Section */}
      <section className="section">
        <div className="container">
          <div className="grid grid-2">
            <div className="about-content animate-on-scroll slide-left">
              <h2 className="section-title" style={{ textAlign: 'left' }}>Why Choose Osley & Associates?</h2>
              <p className="about-text">
                With over 34 years of experience in luxury home construction, we've built our reputation on delivering exceptional craftsmanship, innovative design, and unparalleled client service.
              </p>
              <div className="about-features">
                <div className="feature-item floating-element">
                  <span className="feature-icon">✓</span>
                  <span className="feature-text">Premium materials and finishes</span>
                </div>
                <div className="feature-item floating-element">
                  <span className="feature-icon">✓</span>
                  <span className="feature-text">On-time project delivery</span>
                </div>
                
              </div>
              <Link to="/team" className="btn btn-secondary meet-team-btn">Meet Our Team</Link>
            </div>
            <div className="about-image">
              <img 
                src="homehouse.webp" 
                alt="Luxury home construction" 
                className="about-img"
              />
            </div>
          </div>
        </div>
      </section>

      <style jsx>{`
        .home-page {
          padding-top: 80px;
        }

        .hero-buttons {
          display: flex;
          gap: 20px;
          justify-content: center;
          margin-top: 30px;
        }

        .service-card {
          text-align: center;
          transition: all 0.6s cubic-bezier(0.4, 0, 0.2, 1);
          position: relative;
          overflow: hidden;
        }

        .service-card::before {
          content: '';
          position: absolute;
          top: 0;
          left: 0;
          right: 0;
          bottom: 0;
          background: linear-gradient(135deg, rgba(30, 58, 138, 0.05) 0%, rgba(217, 119, 6, 0.05) 100%);
          opacity: 0;
          transition: opacity 0.3s ease;
        }

        .service-card:hover::before {
          opacity: 1;
        }

        .service-card:hover {
          transform: translateY(-15px) rotateX(5deg);
          box-shadow: 0 25px 50px rgba(0, 0, 0, 0.2);
        }

        .service-icon {
          font-size: 4rem;
          margin-bottom: 25px;
          transition: all 0.4s ease;
          position: relative;
          z-index: 1;
        }

        .service-card:hover .service-icon {
          transform: scale(1.2) rotate(10deg);
          filter: drop-shadow(0 10px 20px rgba(217, 119, 6, 0.3));
        }

        .service-title {
          font-size: 1.5rem;
          color: var(--primary-blue);
          margin-bottom: 15px;
          font-family: 'Playfair Display', serif;
          transition: color 0.3s ease;
          position: relative;
          z-index: 1;
        }

        .service-card:hover .service-title {
          color: var(--gold-accent);
        }

        .service-description {
          color: var(--text-light);
          line-height: 1.6;
          position: relative;
          z-index: 1;
          transition: color 0.3s ease;
        }

        .service-card:hover .service-description {
          color: var(--text-dark);
        }

        .project-title {
          font-size: 1.5rem;
          margin-bottom: 10px;
          font-family: 'Playfair Display', serif;
        }

        .project-description {
          margin-bottom: 15px;
          line-height: 1.5;
        }

        .project-meta {
          display: flex;
          justify-content: space-between;
          font-size: 0.875rem;
          font-weight: 600;
        }

        .project-category {
          color: var(--light-gold);
        }

        .project-size {
          color: var(--light-blue);
        }

        .about-content {
          display: flex;
          flex-direction: column;
          justify-content: center;
        }

        .about-text {
          font-size: 1.125rem;
          line-height: 1.7;
          color: var(--text-light);
          margin-bottom: 30px;
        }

        .about-features {
          margin-bottom: 30px;
        }

        .feature-item {
          display: flex;
          align-items: center;
          gap: 15px;
          margin-bottom: 15px;
          transition: all 0.3s ease;
        }

        .feature-item:hover {
          transform: translateX(10px);
        }

        .feature-icon {
          display: flex;
          align-items: center;
          justify-content: center;
          width: 28px;
          height: 28px;
          background: linear-gradient(135deg, var(--gold-accent) 0%, var(--light-gold) 100%);
          color: var(--white);
          border-radius: 50%;
          font-size: 0.875rem;
          font-weight: 700;
          flex-shrink: 0;
          transition: all 0.3s ease;
        }

        .feature-item:hover .feature-icon {
          transform: scale(1.2);
          box-shadow: 0 0 20px rgba(217, 119, 6, 0.5);
        }

        .feature-text {
          color: var(--text-dark);
          font-weight: 500;
          transition: color 0.3s ease;
        }

        .feature-item:hover .feature-text {
          color: var(--gold-accent);
        }

        .about-image {
          position: relative;
          overflow: hidden;
          border-radius: 20px;
        }

        .about-img {
          width: 100%;
          height: 100%;
          object-fit: cover;
          transition: none;
        }

        .about-image:hover .about-img {
          transform: none;
        }

        .about-image::before {
          display: none;
        }

        .meet-team-btn {
          color: var(--primary-blue) !important;
          border-color: var(--primary-blue) !important;
          background: var(--white) !important;
          font-weight: 600;
        }
        .meet-team-btn:hover {
          background: var(--primary-blue) !important;
          color: var(--white) !important;
        }
        .section {
          margin-bottom: 60px;
        }
        .section:last-of-type {
          margin-bottom: 0;
        }

        .review-card {
          background: var(--white);
          border-radius: 16px;
          box-shadow: 0 4px 15px rgba(0,0,0,0.08);
          padding: 16px 18px;
          margin-bottom: 18px;
          min-width: 0;
          max-width: 100%;
          height: 100%;
          display: flex;
          flex-direction: column;
          justify-content: flex-start;
        }
        .review-text {
          font-size: 1.2rem;
          color: var(--text-dark);
          margin-bottom: 18px;
          font-style: italic;
        }
        .review-author {
          color: var(--gold-accent);
          font-weight: 600;
          font-size: 1rem;
        }
        @media (max-width: 768px) {
          .hero-buttons {
            flex-direction: column;
            align-items: center;
          }

          .about-content {
            order: 2;
          }

          .about-image {
            order: 1;
            margin-bottom: 30px;
          }
          .grid-2 {
            grid-template-columns: 1fr;
          }
        }
      `}</style>
    </div>
  );
};

export default Home;